import { E_QUERY_ITEM_CONDITION, I18nLang, IDrugDevStatusSearchField, IQueryItemField } from '@patsnap/synapse_common_interface'
import type { IBAcTag } from '@pharmsnap/shared/src/types/autocomplete'
import {
  convertCustomDate,
  DrugFinderExpertKey,
  drugFinderExpertReverseMap,
  DRUG_MILESTONE_PHASE_FIELDS,
  generateAllTag,
  generateHardData,
  generateInputItem,
  generateOrgTypeItem,
  generateTagItem,
  ILogicKey,
} from '@pharmsnap/shared/src/utils'
import { ref } from '@vue/composition-api'
import { cloneDeep } from 'lodash'
import { useLocale } from 'pharmsnapMF_shared/composition'
// 导入重构后的模块 - 使用相对路径
import {
  convertToQueryString as convertToQueryStringOptimized,
  convertToStandardQueryItemsOptimized,
  useFieldSpecialHandlers,
} from '../components/finder/drugFinderComp/components/DrugFinderExpertSearch'
import { setGroupField } from '../components/finder/drugFinderComp/components/DrugFinderExpertSearch/config/fieldConfig'
import { IQueryItemWithMeta } from '../components/finder/drugFinderComp/components/DrugFinderExpertSearch/utils/queryTransformHelpers'

export type IQueryItemGroup = IQueryItemField

export type IFieldGroupFieldsItem = { tags?: IBAcTag[]; fields: string[]; selectedValues?: IBAcTag[] }

export type IFieldGroupMeta = {
  originalIndex: number
  originalLogic: 'AND' | 'OR' | 'NOT'
  originalType: 'simple' | 'combined'
  originalProperty: string
  originalId: string
  fieldKey: DrugFinderExpertKey
}

/**
 * notExpertFinderItems: 不在组合检索的检索项中，但是出现在query里，需要支持这部分的query 文本显示
 * 比如药物研发状态列表，选择了过滤项，再点击跳到药物列表，这些过滤项会增加在must中
 */
export type IFieldGroupFieldsKey = DrugFinderExpertKey | 'notExpertFinderItems'

export interface IFieldGroup {
  id: string
  logic: 'AND' | 'OR' | 'NOT'
  type: 'simple' | 'combined'
  property: string
  _meta?: IFieldGroupMeta
  isNotFirstGroup?: boolean
  fields: Partial<Record<IFieldGroupFieldsKey, IFieldGroupFieldsItem>>
}

export const fieldGroupsBasis = Object.freeze([
  {
    id: '1',
    logic: 'AND',
    type: 'simple',
    property: 'drug',
    fields: {
      drugItems: {
        fields: ['DRUG_ID'],
        tags: [],
      },
    },
  },
  {
    id: '2',
    logic: 'AND',
    type: 'simple',
    property: 'drugType',
    fields: {
      drugTypeItems: {
        fields: ['DRUG_TYPE'],
        tags: [],
      },
    },
  },
  {
    id: '3',
    logic: 'AND',
    type: 'simple',
    property: 'target',
    fields: {
      targetItems: {
        fields: ['TARGET_ID'],
        tags: [],
      },
    },
  },
  {
    id: '4',
    logic: 'AND',
    type: 'simple',
    property: 'atc',
    fields: {
      atcItems: { fields: ['ATC_CODE'], tags: [] },
    },
  },
  {
    id: '5',
    logic: 'AND',
    type: 'simple',
    property: 'chemicalModification',
    fields: {
      modificationItems: {
        fields: ['MODIFICATION_ID'],
        tags: [],
      },
    },
  },
  {
    id: '6',
    logic: 'AND',
    type: 'simple',
    property: 'keyword',
    fields: {
      allFieldItems: { fields: ['ALL_FIELDS'], tags: [] },
    },
  },
  {
    id: '7',
    logic: 'AND',
    type: 'simple',
    property: 'deal',
    fields: {
      dealExistItems: {
        fields: ['EXIST_DEAL'],
        selectedValues: [generateAllTag()],
      },
    },
  },
  {
    id: '8',
    logic: 'AND',
    type: 'combined',
    property: 'indication',
    fields: {
      indicationItems: { fields: ['RESEARCH_DISEASE', 'NON_RESEARCH_DISEASE'], tags: [] },
      indicationPhaseItems: {
        fields: ['INDICATION_PHASE'],
        selectedValues: [generateAllTag()],
      },
    },
  },
  {
    id: '9',
    logic: 'AND',
    type: 'combined',
    property: 'moa',
    fields: {
      actionItems: {
        fields: ['ACTION_ID'],
        tags: [],
      },
      mechanismItems: {
        fields: ['MECHANISM_ACTION_ID'],
        tags: [],
      },
    },
  },
  {
    id: '10',
    logic: 'AND',
    type: 'combined',
    property: 'milestone',
    fields: {
      milestoneItems: {
        fields: ['MILESTONE_ID'],
        selectedValues: [generateAllTag()],
      },
      milestoneDate: { fields: ['MILESTONE_DATE'], selectedValues: [] },
    },
  },
  {
    id: '11',
    logic: 'AND',
    type: 'simple',
    property: 'highestPhase',
    fields: {
      highestPhaseItems: {
        fields: ['GLOBAL_HIGHEST_DEV_STATUS'],
        selectedValues: [generateAllTag()],
      },
    },
  },
  {
    id: '12',
    logic: 'AND',
    type: 'combined',
    property: 'organization',
    fields: {
      organizationItems: {
        fields: ['DEV_ORG_ID_ONEID', 'NON_DEV_ORG_ID_ONEID', 'ORIGINAL_DEV_ORG_ID_ONEID', 'LICENSE_ORG_ID_ONEID'],
        tags: [],
      },
      orgRoleItems: {
        fields: ['DEV_ORG_TYPE_ONEID'],
        selectedValues: [generateAllTag()],
      },
    },
  },
  {
    id: '13',
    logic: 'AND',
    type: 'combined',
    property: 'organizationProperty',
    fields: {
      organizationTypeItems: {
        fields: ['ORG_TYPE_ONEID'],
        selectedValues: [generateAllTag()],
      },
      employeeNumberItems: {
        fields: ['ORG_EMP_NUMBER_GENERAL_ONEID'],
        selectedValues: [generateAllTag()],
      },
      startupItems: {
        fields: ['DEV_STATUS_ORG_IS_STARTUPS'],
        selectedValues: [],
      },
    },
  },
  {
    id: '14',
    logic: 'AND',
    type: 'combined',
    property: 'currentRdStatus',
    fields: {
      indicationItems: {
        fields: ['DISEASE_ID'],
        tags: [],
      },
      organizationItems: {
        fields: ['ORGANIZATION_ID_ONEID'],
        tags: [],
      },
      phaseItems: {
        fields: ['DEV_STATUS'],
        selectedValues: [generateAllTag()],
      },
      approvedCountryItems: { fields: ['COUNTRY_COLLAPSED'], selectedValues: [generateAllTag()] },
      milestoneItems: { fields: ['MILESTONE_ID'], selectedValues: [generateAllTag()] },
      dateItems: {
        fields: ['DEV_STATUS_TIME'],
        selectedValues: [],
      },
    },
  },
] as IFieldGroup[])

const logicOptions = [
  {
    label: 'AND',
    value: 'AND',
  },
  {
    label: 'OR',
    value: 'OR',
  },
  {
    label: 'NOT',
    value: 'NOT',
  },
]
export function useDrugFinderExpertSearch() {
  const logicConfig = ref<Record<Extract<ILogicKey, 'allFieldItems'>, E_QUERY_ITEM_CONDITION>>({
    allFieldItems: E_QUERY_ITEM_CONDITION.ANY,
  })
  const { locale, ts } = useLocale()
  const { restoreIndicationPhaseItems, restoreOrgRoleItems } = useFieldSpecialHandlers()
  const localeValue = ref<I18nLang>(locale.value)

  // ========== 使用重构后的转换函数 ==========
  // 使用优化的 convertToQueryString (消除硬编码映射)
  const convertToQueryString = (groups: IFieldGroup[]) => {
    return convertToQueryStringOptimized(groups, localeValue.value)
  }

  // ========== 使用重构后的转换函数 ==========
  // 使用优化的 convertToStandardQueryItems (使用统一配置，消除硬编码映射)
  const convertToStandardQueryItems = (groups: IFieldGroup[]) => {
    return convertToStandardQueryItemsOptimized(groups)
  }

  // 从字段名推断 _meta 的辅助函数
  const inferMetaFromField = (item: IQueryItemWithMeta): IFieldGroupMeta | null => {
    // 检查 item 是否有 fields 属性 (IQueryItemField 才有, IQueryItemEntity 没有)
    if (!('fields' in item) || !item.fields || item.fields.length === 0) return null
    const fieldName = item.fields[0]
    const config = drugFinderExpertReverseMap[fieldName as keyof typeof drugFinderExpertReverseMap]
    if (!config) return null

    // 从 fieldGroupsBasis 中查找对应的 property
    // fieldKey (如 targetItems) 对应 fieldGroupsBasis 中的 property (如 target)
    const fieldGroup = fieldGroupsBasis.find((g) => g.fields[config.key as keyof typeof g.fields])
    const property = fieldGroup?.property || config.key

    return {
      originalIndex: 0,
      originalLogic: 'AND',
      originalType: 'simple',
      originalProperty: property,
      originalId: '',
      fieldKey: config.key as DrugFinderExpertKey,
    }
  }

  type IQueryItemValue = { id: string; display_name_cn: string; display_name_en: string; value: string }
  // 根据meta信息创建字段组
  const createGroupFromMeta = (meta: IFieldGroupMeta, item: any): IFieldGroup => {
    const group: IFieldGroup = {
      id: meta.originalId,
      logic: meta.originalLogic,
      type: meta.originalType,
      property: meta.originalProperty,
      fields: {},
    }

    // 恢复字段数据
    if (item.value && item.value.length > 0) {
      // 从fieldGroupsBasis.fields中获取fieldKey对应的tag, 如果存在,则恢复为tags格式, 如果不存在,则恢复为selectedValues格式
      const isTagType = fieldGroupsBasis.find((field) => field.fields[meta.fieldKey]?.tags)

      if (isTagType) {
        // 恢复为tags格式
        group.fields[meta.fieldKey] = {
          fields: item.fields,
          tags: item.value.map((val: IQueryItemValue) => ({
            ...val,
            id: val.id || val.value,
            display_name_cn: val.display_name_cn,
            display_name_en: val.display_name_en,
          })),
        }
      } else {
        // 里程碑字段需要单独处理
        if (meta.fieldKey === 'milestoneDate') {
          group.fields[meta.fieldKey] = {
            fields: ['MILESTONE_DATE'],
            selectedValues: item.value.map((val: IQueryItemValue) => ({
              ...val,
              id: val.id || val.value,
              display_name_cn: val.display_name_cn,
              display_name_en: val.display_name_en,
            })),
          }
          group.fields.milestoneItems = {
            fields: ['MILESTONE_ID'],
            selectedValues: item.fields.map((val: string) => generateHardData('milestoneItems').find((o) => o.id === val)),
          }
        } else {
          // 恢复为selectedValues格式
          group.fields[meta.fieldKey] = {
            fields: item.fields,
            selectedValues: item.value.map((val: IQueryItemValue) => ({
              ...val,
              id: val.id || val.value,
              display_name_cn: val.display_name_cn,
              display_name_en: val.display_name_en,
            })),
          }
        }
      }
    }

    return group
  }

  // 将标准查询项恢复为fieldGroupsBasis格式
  const convertFromStandardQueryItems = (queryItems: IQueryItemWithMeta, skipNotExpertFinderItems = false): IFieldGroup[] => {
    if (!queryItems) return []

    const result: IFieldGroup[] = []

    // 递归处理查询项
    const processQueryItem = (item: IQueryItemWithMeta, defaultLogic: 'AND' | 'OR' | 'NOT' = 'AND'): void => {
      if (item.type === 'field') {
        if (item._meta) {
          // 处理单个字段项
          const group = transformFieldFromQueryItems(createGroupFromMeta(item._meta, item), item, item._meta)

          // 根据condition判断逻辑
          if (item.condition === 'none') {
            group.logic = 'NOT'
          } else {
            group.logic = defaultLogic
          }
          group._meta = item._meta
          result.push(group)
        } else {
          // 尝试从字段名推断 _meta
          const inferredMeta = inferMetaFromField(item)
          if (inferredMeta) {
            // 使用推断的 meta 来处理
            const group = transformFieldFromQueryItems(createGroupFromMeta(inferredMeta, item), item, inferredMeta)
            group.logic = defaultLogic
            group._meta = inferredMeta
            result.push(group)
          } else if (!skipNotExpertFinderItems) {
            result.push({
              id: '',
              logic: 'AND',
              type: 'simple',
              property: 'notExpertFinderItems',
              fields: {
                notExpertFinderItems: {
                  fields: item.fields,
                  tags: generateTagItem(item),
                },
              },
            })
          }
        }
      } else if (item.type === 'group') {
        if (item.must) {
          // 处理must数组：AND逻辑
          item.must.forEach((subItem: IQueryItemWithMeta, index: number) => {
            processQueryItem(subItem, index === 0 ? 'AND' : 'AND')
          })
        } else if (item.any) {
          // 处理any数组：OR逻辑
          item.any.forEach((subItem: IQueryItemWithMeta, index: number) => {
            processQueryItem(subItem, index === 0 ? 'AND' : 'OR')
          })
        }
      }
    }

    // 处理根级别的查询项
    if (queryItems.type === 'group' && queryItems.must) {
      queryItems.must.forEach((item: IQueryItemWithMeta, index: number) => {
        processQueryItem(item, index === 0 ? 'AND' : 'AND')
      })
    } else if (queryItems.type === 'group' && queryItems.any) {
      queryItems.any.forEach((item: IQueryItemWithMeta, index: number) => {
        processQueryItem(item, index === 0 ? 'AND' : 'OR')
      })
    } else {
      processQueryItem(queryItems, 'AND')
    }
    // result 按字段id相等,type=combined的从新组合group
    const newResult: IFieldGroup[] = []
    result.forEach((item) => {
      const fieldGroup = fieldGroupsBasis.find((g) => g.property === item.property && g.type === 'combined')
      const index = newResult.findIndex((i) => i.id === item.id)
      if (index === -1) {
        if (item.type === 'combined') {
          item.fields = {
            ...cloneDeep(fieldGroup?.fields),
            ...item.fields,
          }
        }
        newResult.push(item)
      } else {
        // 已存在则合并fields
        newResult[index].fields = {
          ...newResult[index].fields,
          ...item.fields,
        }
      }
    })
    // 处理combined类型的数据, 恢复为fieldGroupsBasis格式，
    // 并恢复logic，selectedValues
    // 针对indication和organization的selectedValues进行处理
    const translateNewResult = newResult.map((item) => {
      const fieldGroup = fieldGroupsBasis.find((g) => g.property === item.property && g.type === 'combined')
      if (fieldGroup) {
        // 新增分组
        if (fieldGroup) {
          item.logic = item._meta?.originalLogic || 'AND'
          // 合并fields，优先保留item中的数据
          // indication 相关逻辑
          if (item.property === 'indication') {
            restoreIndicationPhaseItems(item)
          }

          // organization 相关逻辑
          if (item.property === 'organization') {
            restoreOrgRoleItems(item)
          }
        }
      }
      return item
    })

    // 按原始ID排序
    return translateNewResult.sort((a, b) => parseInt(a.id) - parseInt(b.id))
  }
  const transformFieldFromQueryItems = (group: IFieldGroup, item: IQueryItemField, meta: IFieldGroupMeta) => {
    const data = group.fields
    const fieldKey = meta?.fieldKey
    if (!fieldKey) return group

    // 恢复高级检索Tab的数据
    if (item.type === 'field') {
      // 非hot的数据走这边构造
      const fields = item.fields

      const configKey = DRUG_MILESTONE_PHASE_FIELDS.includes(fields[0] as IDrugDevStatusSearchField) ? 'MILESTONE_DATE' : fields[0]
      const config = drugFinderExpertReverseMap[configKey as keyof typeof drugFinderExpertReverseMap]
      if (config) {
        // const items = item.value
        // if (config.key === 'drugItems' && items.length > maxDrugCount) {
        //   showSingleToast({
        //     message: ts('common.drugLimitedMessage', { count: maxDrugCount }) as string,
        //     type: 'info',
        //   })
        // }
        if (config.dataType === 'input') {
          const newItems = generateInputItem(item, config.icon)
          setGroupField(group, fieldKey, {
            fields: item.fields,
            tags: newItems,
          })
        }
        if (config.dataType === 'tag') {
          const newItems = generateTagItem(item)
          setGroupField(group, fieldKey, {
            fields: item.fields,
            selectedValues: newItems,
          })
        }
        if (config.dataType === 'orgType') {
          const newItems = generateOrgTypeItem(item)
          setGroupField(group, fieldKey, {
            fields: item.fields,
            selectedValues: newItems,
          })
        }
        if (config.dataType === 'date') {
          setGroupField(group, fieldKey, {
            fields: item.fields,
            selectedValues: convertCustomDate(item, true),
          })
        }
      }
    }
    return group
  }
  const handleChangeLogicVal = (fieldKey: Extract<ILogicKey, 'allFieldItems'>, val: E_QUERY_ITEM_CONDITION) => {
    logicConfig.value[fieldKey] = val
  }

  const convertFromStandardQueryItemsToQueryString = (queryItems: IQueryItemWithMeta[], locale?: I18nLang): string => {
    localeValue.value = locale || localeValue.value
    const expertStandGroupQuery = queryItems.length > 1 ? { type: 'group', must: queryItems } : queryItems[0]
    const result = convertFromStandardQueryItems(expertStandGroupQuery as IQueryItemWithMeta)
    return convertToQueryString(result)
  }

  return {
    logicConfig,
    convertToQueryString,
    convertToStandardQueryItems,
    convertFromStandardQueryItems,
    convertFromStandardQueryItemsToQueryString,
    handleChangeLogicVal,
    logicOptions,
  }
}
